import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ProgressContext = createContext(null);

const KINDS = ["lesson", "flashcards", "quiz", "scenario", "summary"];

export function ProgressProvider({ children }) {
  const { user } = useAuth();
  const [views, setViews] = useState([]); // [{chapter_id, kind, view_count, last_viewed_at}]
  const [quizBest, setQuizBest] = useState([]); // [{chapter_id, score, total, percentage}]
  const [quizHistory, setQuizHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.role === "admin") {
      setViews([]); setQuizBest([]); setQuizHistory([]); setLoaded(true);
      return;
    }
    try {
      const r = await api.get("/progress/me");
      setViews(r.data.views || []);
      setQuizBest(r.data.quiz_best || []);
      setQuizHistory(r.data.quiz_history || []);
    } catch (err) {
      // Non-fatal: progress fetch failure just means empty stats until next refresh.
      console.warn("Progress refresh failed:", err?.message);
    }
    finally { setLoaded(true); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const recordView = useCallback(async (chapter_id, kind) => {
    if (!user || user.role === "admin") return;
    try {
      await api.post("/progress/view", { chapter_id, kind });
      setViews((prev) => {
        const existing = prev.find((v) => v.chapter_id === chapter_id && v.kind === kind);
        if (existing) {
          return prev.map((v) =>
            v.chapter_id === chapter_id && v.kind === kind
              ? { ...v, view_count: (v.view_count || 0) + 1, last_viewed_at: new Date().toISOString() }
              : v
          );
        }
        return [...prev, { chapter_id, kind, view_count: 1, first_viewed_at: new Date().toISOString(), last_viewed_at: new Date().toISOString() }];
      });
    } catch (err) {
      console.warn("recordView failed:", err?.message);
    }
  }, [user]);

  const recordQuiz = useCallback(async (chapter_id, score, total) => {
    if (!user || user.role === "admin") return;
    try {
      const r = await api.post("/progress/quiz", { chapter_id, score, total });
      setQuizHistory((h) => [{ chapter_id, score, total, percentage: r.data.percentage, completed_at: new Date().toISOString() }, ...h]);
      setQuizBest((prev) => {
        const existing = prev.find((b) => b.chapter_id === chapter_id);
        const pct = r.data.percentage;
        if (!existing) return [...prev, { chapter_id, score, total, percentage: pct, achieved_at: new Date().toISOString() }];
        if (pct > existing.percentage) {
          return prev.map((b) => (b.chapter_id === chapter_id ? { ...b, score, total, percentage: pct, achieved_at: new Date().toISOString() } : b));
        }
        return prev;
      });
    } catch (err) {
      console.warn("recordQuiz failed:", err?.message);
    }
  }, [user]);

  const chapterSummary = useCallback((chapter_id) => {
    const seen = new Set(views.filter((v) => v.chapter_id === chapter_id).map((v) => v.kind));
    const viewedKinds = KINDS.filter((k) => seen.has(k));
    const best = quizBest.find((b) => b.chapter_id === chapter_id);
    return {
      viewedKinds,
      viewedCount: viewedKinds.length,
      totalKinds: KINDS.length,
      percentage: Math.round((viewedKinds.length / KINDS.length) * 100),
      quizBest: best || null,
    };
  }, [views, quizBest]);

  const value = useMemo(
    () => ({ views, quizBest, quizHistory, loaded, refresh, recordView, recordQuiz, chapterSummary }),
    [views, quizBest, quizHistory, loaded, refresh, recordView, recordQuiz, chapterSummary],
  );

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error("useProgress must be inside ProgressProvider");
  return ctx;
}

export { KINDS };
